// 多语言国际化辅助脚本

import '../../@types';
import { isInWujie } from './env';
import { getQueryParam } from './tools';

declare interface Window extends globalThis.Window {
    language_data?: LanguageData;
    language?: LanguageData;
}
declare const window: Window;

// const language_data = window.language_data || window.language || ({} as LanguageData);

/** 获取用户语言，这里获得的是 ISO-639-1 或 RFC-5646 */
export function getUserLanguage() {
    let lang = 'en';
    if (isInWujie()) lang = $wujie.props.getUserLanguage();
    else lang = getQueryParam('lang', 'lan') ?? navigator.language;
    console.debug('[Act][getUserLanguage] 用户语言', `lang = ${lang}`);
    return lang;
}

/** 游戏语言枚举 */
export enum GameLanguage {
    Unknown = 0, // 枚举需与CI18NDefine保持一致
    CHS = 1, // 简体中文
    EN = 2, // 英语
    JA = 3, // 日语
    KO = 4, // 韩语
    CHT = 5, // 繁体中文
    FR = 6, // 法语
    DE = 7, // 德语
    ES = 8, // 西班牙语
    IT = 9, // 意大利语
    TH = 10, // 泰语
    VI = 11, // 越南语
    RU = 12, // 俄语
    TR = 13, // 土耳其语
    PT = 14, // 葡萄牙语
    PT_BR = 15, // 巴西葡萄牙语
    ES_LT = 16, // 拉美西语
    NL = 17, // 荷兰语
    MS = 18, // 马来语
    ID = 19, // 印尼语
    AR = 20, // 阿拉伯语
    HI = 21, // 印地语
    EN_US = 22, // 美式英语
    BN = 23, // 孟加拉语
    MY = 24, // 缅甸语
    LO = 25, // 老挝语
    KM = 26, // 高棉语
    MAX = 27,
}

export const GameLanguageToAmsTips = {
    1: 'zh', // CHS
    2: 'en', // EN,
    3: 'jp', // JP
    4: 'kr', // KO,
    5: 'tw', // CHT,
    6: 'fr', // FR,
    7: 'de', // DE,
    8: 'es', // ES,
    16: 'es', // ESLT,
    10: 'tha', // TH,
    11: 'vi', // VI,
    12: 'ru', // RU,
    13: 'tr', // TR,
    14: 'po', // PT,
    15: 'po', // PTBR,
    18: 'ms', // MS 马来语
    19: 'id', // ID,
    20: 'ar', // AR,
    0: 'en', // Unknown,
    9: 'en', // IT,
    17: 'en', // NL,
    21: 'en', // HI,
    22: 'en', // LAN00,
    23: 'en', // LAN01,
    24: 'en', // LAN02,
    25: 'en', // LAN03,
    26: 'en', // Count,
};

declare type GameLanguageName = 'UNKNOWN' | keyof typeof GameLanguage;

const GameLanguageToWebLanguage: { [k in GameLanguageName]: string | string[] } = {
    // @ts-ignore
    UNKNOWN: 'unknown',
    Unknown: 'unknown',
    MAX: 'max',
    CHS: ['chs', 'cn', 'zh', 'zh-cn', 'zh-hans'], // 简体中文
    CHT: ['cht', 'tc', 'zh-tw', 'zh-hk'], // 繁体中文
    EN: 'en', // 英语
    JA: 'ja', // 日语
    KO: ['ko', 'kr', 'ko-kr'], // 韩语
    FR: 'fr', // 法语
    DE: 'de', // 德语
    ES: 'es', // 西班牙语
    IT: 'it', // 意大利语
    TH: 'th', // 泰语
    VI: 'vi', // 越南语
    RU: 'ru', // 俄语
    TR: 'tr', // 土耳其语
    PT: 'pt', // 葡萄牙语
    PT_BR: ['ptbr', 'pt-br', 'pt_br'], // 巴西葡萄牙语
    ES_LT: ['es', 'es-lt', 'es_lt'], // 拉美西语
    NL: 'nl', // 荷兰语
    MS: 'ms', // 马来语
    ID: 'id', // 印尼语
    AR: 'ar', // 阿拉伯语
    HI: 'hi', // 印地语
    EN_US: 'en_us', // 美式英语
    BN: 'bn', // 孟加拉语
    MY: 'my', // 缅甸语
    LO: 'lo', // 老挝语
    KM: 'km', // 高棉语
};

/** 获取游戏语言ID */
export function getGameLanguageId(lang: string): number {
    lang = lang.toUpperCase();
    if (GameLanguageToWebLanguage[lang] != null) return GameLanguage[lang];
    lang = lang.toLowerCase();
    for (const [g, w] of Object.entries(GameLanguageToWebLanguage)) {
        if (lang === w || w.indexOf(lang) >= 0) return GameLanguage[g];
    }
    return GameLanguage.Unknown;
}

/** 获取游戏语言名称 */
export function getGameLanguageName(id: number | string): GameLanguageName {
    let name: GameLanguageName = 'Unknown';
    if (!isNaN(+id)) {
        // 这是数字ID
        if (GameLanguage[id] != null) name = GameLanguage[id];
    } else {
        // 这是非数字
        id = getGameLanguageId(String(id));
        name = GameLanguage[id] as any;
    }
    console.debug('[Act][getGameLanguageName] 获取到对应的游戏语言名称', `id = ${id}, name = ${name}`);
    return name;
}

/** 获取用户当前语言对应的游戏语言名称 */
export function getUserGameLanguage(): GameLanguageName {
    return getGameLanguageName(getUserLanguage());
}

/** 获取某个游戏语言对应的 AMS 语言提示名称 */
export function getAmsLanguageTips(lang: GameLanguageName | number): string {
    const v = GameLanguageToAmsTips[lang];
    if (v != null) return v;
    lang = getGameLanguageId(lang as string);
    return GameLanguageToAmsTips[lang] || 'en';
}

/** 获取对应的语言，data 为对应多语言配置，lang 为想要获取的语言，返回实际对应多语言配置中的语言名称 */
export function getLanguage(data: LanguageData, lang: string): LangType {
    if (data[lang]) return lang as LangType;
    lang = lang.toLowerCase();
    if (data[lang]) return lang as LangType;
    // 不同语言简称兼容
    // en_us 不存在时使用 enus/en
    if (lang === 'en_us') lang = (data['enus'] && 'enus') || 'en';
    // enus 不存在时使用 en_us/en
    else if (lang === 'enus') lang = (data['en_us'] && 'en_us') || 'en';
    // 葡语 PTBR/PT
    // ptbr 不存在时使用 pt
    else if (lang === 'ptbr') lang = data.pt && 'pt';
    // pt 不存在时使用 ptbr
    else if (lang === 'pt') lang = data['ptbr'] && 'ptbr';
    // pt-br 不存在时使用 ptbr/pt
    else if (lang === 'pt-br') lang = (data['ptbr'] && 'ptbr') || (data['pt'] && 'pt');
    // 简体 CHS/CN/ZH/ZH-CN
    // cn 不存在时按顺序使用 zh-cn、zh、chs
    else if (lang === 'cn') lang = (data['zh-cn'] && 'zh-cn') || (data['zh'] && 'zh') || (data.chs && 'chs');
    // zh 不存在时按顺序使用 zh-cn、cn、chs
    else if (lang === 'zh') lang = (data['zh-cn'] && 'zh-cn') || (data['cn'] && 'cn') || (data.chs && 'chs');
    // chs 不存在时按顺序使用 zh-cn、zh、cn
    else if (lang === 'chs') lang = (data['zh-cn'] && 'zh-cn') || (data['zh'] && 'zh') || (data['cn'] && 'cn');
    // zh-cn 不存在时按顺序使用 zh/cn/chs
    else if (lang === 'zh-cn') lang = (data['zh'] && 'zh') || (data['cn'] && 'cn') || (data.chs && 'chs');
    // cht 不存在时按顺序使用 tc/chs
    else if (lang === 'cht') lang = (data['tc'] && 'tc') || (data['chs'] && 'chs');
    // tc 不存在时按顺序使用 cht/chs
    else if (lang === 'tc') lang = (data['cht'] && 'cht') || (data['chs'] && 'chs');
    // zh-tw 不存在时按顺序使用 tc/cht/chs
    else if (lang === 'zh-tw') lang = (data['tc'] && 'tc') || (data['cht'] && 'cht') || (data.chs && 'chs');
    // zh-hk 不存在时按顺序使用 tc/cht/chs
    else if (lang === 'zh-hk') lang = (data['tc'] && 'tc') || (data['cht'] && 'cht') || (data.chs && 'chs');
    // es 不存在时
    else if (lang === 'es') lang = (data['eslt'] && 'eslt') || (data['es_lt'] && 'es_lt') || (data['es-lt'] && 'es-lt');
    // es-lt 不存在时使用 eslt/es
    else if (lang === 'es-lt') lang = (data['eslt'] && 'eslt') || (data['es'] && 'es') || (data['es_lt'] && 'es_lt');
    // es_lt 不存在时使用 eslt/es
    else if (lang === 'es_lt') lang = (data['eslt'] && 'eslt') || (data['es'] && 'es') || (data['es-lt'] && 'es-lt');
    // eslt 不存在时使用 es_lt/es
    else if (lang === 'eslt') lang = (data['es_lt'] && 'es_lt') || (data['es'] && 'es') || (data['es-lt'] && 'es-lt');
    // kr 不存在时使用 ko
    else if (lang === 'kr') lang = data['ko'] && 'ko';
    // ko-kr 不存在时使用 kr/ko
    else if (lang === 'ko-kr') lang = (data['kr'] && 'kr') || (data['ko'] && 'ko');
    // de 不存在时使用ge
    else if (lang === 'de') lang = data['ge'] && 'ge';
    // 日语匹配
    else if (lang === 'ja') lang = data['jp'] && 'jp';
    else if (lang === 'jp') lang = data['ja'] && 'ja';
    // 马来语
    else if (lang === 'my') lang = data['ms'] && 'ms';
    else if (lang === 'ms') lang = data['my'] && 'my';
    // 如果不匹配任何语言，返回默认语言 en
    return data[lang] ? (lang as LangType) : 'en';
}

/** 获取本地默认的语言设置，优先从链接参数（默认参数名由 queryParamName 指定，默认为 lang）获取，无法获取时从本地化设置获取，languageData 为支持的多语言配置数据 */
export function getCurrentLanguage(languageData: LanguageData, ...queryParamNames: string[]): LangType {
    // 默认参数名为 lang
    if (queryParamNames?.length < 1) queryParamNames = ['lang'];
    let lang: string | undefined;
    for (const n of queryParamNames) {
        lang = getQueryParam(n);
        if (lang) break;
    }
    console.info('[Act] 从链接参数', queryParamNames, '中查找到支持的语言', lang);
    // 如果无法获取则尝试从系统语言中获取
    if (!lang) {
        const allLangs = Object.keys(languageData).map((l) => l.toLowerCase());
        for (let s of navigator.languages) {
            s = s.toLowerCase();
            // 直接包含对应配置的语言
            if (allLangs.indexOf(s) > -1) {
                lang = s;
                break;
            }
            const arr = s.split('-');
            if (arr.length <= 1) continue;
            const l1 = arr[0];
            const l2 = arr.pop();
            // 中文
            if (l1 === 'zh') {
                // 繁体中文
                if (l2 === 'hk' || l2 === 'tw') lang = 'cht';
                // 其它默认简体中文
                else lang = 'chs';
                break;
            }
            // 葡语
            lang = l1;
            break;
        }
        console.info('[Act] 从支持的系统语言', navigator.languages, '中查找到支持的语言', lang);
        if (!lang) lang = 'en';
    }
    return getLanguage(languageData, lang);
}

/** 获取指定 key 的某个多语言翻译文本 */
export function getLanguageText(languageData: LanguageData, key: string, lang: LangType): string;
/** 获取指定 key 的某个多语言翻译文本，并将文本里的占位符 plactholder 替换成传入的数据 data */
export function getLanguageText(languageData: LanguageData, key: string, data: string, plactholder: string, lang: LangType): string;
export function getLanguageText(languageData: LanguageData, key: string, data?: string, plactholder?: string, lang?: LangType): string {
    if (arguments.length <= 3) {
        lang = data as LangType;
        return languageData[lang as string]?.[key] ?? '';
    }
    return languageData[lang as string]?.[key]?.replace(plactholder, data);
}
